/**
 * WorkflowsPage (SPEC PX-4 - workflow-builder-2026-06-17).
 *
 * A visual node/branch workflow builder over the EXISTING workflow engine.
 *
 * R1 - the read path: list the tenant's `workflow_definitions`, select one, and
 * render it as a read-only node/branch graph (decompiled from the engine's flat
 * `actions[]` + integer jump-index JSON via `lib/workflow-graph.ts`), with a
 * metadata-driven palette sidebar.
 *
 * R2 - the authoring path: a "New Workflow" / "Edit" path opens the
 * WorkflowEditor (drag from the palette, schema-driven node config, branch /
 * split / wait_for_event authoring, save via POST/PUT, activate via PUT
 * {active:true} with a send-bearing confirm, delete behind an AlertDialog). New
 * definitions are always created active:false (dead-by-default).
 *
 * Gated behind the existing OperatorOnly route wrapper (App.tsx) - a non-operator
 * cannot reach `/workflows`.
 */

import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertCircle,
  GitBranch,
  Plus,
  Power,
  PowerOff,
  RefreshCw,
  SquarePen,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import {
  useUpdateWorkflow,
  useWorkflow,
  useWorkflowMetadata,
  useWorkflows,
} from '@/lib/api/hooks/useWorkflows';
import { definitionToGraph, graphHasSendBearingAction } from '@/lib/workflow-graph';
import { WorkflowCanvas } from '@/components/workflows/WorkflowCanvas';
import { PalettePanel } from '@/components/workflows/PalettePanel';
import { WorkflowEditor } from '@/components/workflows/WorkflowEditor';
import type { WorkflowListItem } from '@/types/workflow';

type EditorState =
  | { mode: 'closed' }
  | { mode: 'new' }
  | { mode: 'edit'; id: string; active: boolean };

export function WorkflowsPage() {
  const { data: workflows, isLoading, error, refetch } = useWorkflows();
  const { data: metadata } = useWorkflowMetadata();
  const updateMutation = useUpdateWorkflow();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState>({ mode: 'closed' });
  const [pendingActivate, setPendingActivate] = useState<WorkflowListItem | null>(null);

  const { data: selectedDef, isLoading: loadingDef } = useWorkflow(selectedId || '');

  const graph = useMemo(
    () => (selectedDef ? definitionToGraph(selectedDef) : null),
    [selectedDef]
  );

  const setActive = async (item: WorkflowListItem, next: boolean) => {
    try {
      await updateMutation.mutateAsync({ id: item.id, data: { active: next } });
      toast.success(next ? 'Workflow activated' : 'Workflow deactivated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update workflow');
    }
  };

  // Row toggle. Activation of a send-bearing definition requires confirm (AC8).
  const handleRowToggle = (item: WorkflowListItem) => {
    if (item.active) {
      setActive(item, false);
      return;
    }
    if (graphHasSendBearingAction(item)) {
      setPendingActivate(item);
      return;
    }
    setActive(item, true);
  };

  // --- Editor view ---------------------------------------------------------
  if (editor.mode !== 'closed') {
    return (
      <WorkflowEditor
        workflowId={editor.mode === 'edit' ? editor.id : undefined}
        initialActive={editor.mode === 'edit' ? editor.active : false}
        onClose={() => setEditor({ mode: 'closed' })}
        onSaved={(id) => {
          // Stay in the editor (now in edit mode for the saved id).
          if (editor.mode === 'new') setEditor({ mode: 'edit', id, active: false });
        }}
        onDeleted={() => {
          setEditor({ mode: 'closed' });
          setSelectedId(null);
        }}
      />
    );
  }

  // --- List + read-only canvas view ---------------------------------------
  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold tracking-tight">Workflows</h1>
            <p className="text-sm text-muted-foreground">
              Visual automation builder over the workflow engine
            </p>
          </div>
          <Button onClick={() => setEditor({ mode: 'new' })} data-testid="new-workflow-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Workflow
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* List column */}
        <div className="w-72 shrink-0 border-r overflow-auto p-3 space-y-2" data-testid="workflow-list">
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : error ? (
            <Card className="p-4 text-center">
              <AlertCircle className="h-8 w-8 mx-auto text-destructive mb-2" />
              <p className="text-sm text-muted-foreground mb-3">
                {error instanceof Error ? error.message : 'Failed to load workflows'}
              </p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </Card>
          ) : !workflows?.length ? (
            <Card className="p-6 text-center">
              <GitBranch className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No workflows yet.</p>
            </Card>
          ) : (
            workflows.map((wf) => (
              <Card
                key={wf.id}
                data-testid={`workflow-row-${wf.id}`}
                className={cn(
                  'transition-shadow',
                  selectedId === wf.id && 'ring-2 ring-primary',
                  !wf.active && 'opacity-70'
                )}
              >
                <CardHeader className="p-3 pb-1">
                  <CardTitle className="text-sm flex items-center justify-between gap-2">
                    <button
                      type="button"
                      className="truncate text-left hover:underline"
                      onClick={() => setSelectedId(wf.id)}
                    >
                      {wf.name}
                    </button>
                    {wf.active ? (
                      <Badge variant="success">Active</Badge>
                    ) : (
                      <Badge variant="neutral">Inactive</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Badge variant="outline">{wf.trigger?.type ?? 'trigger'}</Badge>
                      <span>
                        {wf.actions?.length ?? 0} step
                        {(wf.actions?.length ?? 0) !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title={wf.active ? 'Deactivate' : 'Activate'}
                        data-testid={`row-toggle-${wf.id}`}
                        onClick={() => handleRowToggle(wf)}
                      >
                        {wf.active ? (
                          <Power className="h-4 w-4 text-green-600" />
                        ) : (
                          <PowerOff className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title="Edit"
                        data-testid={`row-edit-${wf.id}`}
                        onClick={() => setEditor({ mode: 'edit', id: wf.id, active: !!wf.active })}
                      >
                        <SquarePen className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Canvas column (read-only preview) */}
        <div className="flex-1 overflow-hidden p-3">
          {!selectedId ? (
            <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
              Select a workflow to view its graph, or create a new one
            </div>
          ) : loadingDef || !graph ? (
            <Skeleton className="h-full w-full" />
          ) : (
            <WorkflowCanvas graph={graph} />
          )}
        </div>

        {/* Metadata-driven palette (non-interactive in the preview). */}
        <PalettePanel metadata={metadata} />
      </div>

      {/* Send-bearing row-activation confirm (Rule 0 - AC8). */}
      <AlertDialog
        open={!!pendingActivate}
        onOpenChange={(o) => !o && setPendingActivate(null)}
      >
        <AlertDialogContent data-testid="row-activate-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Activate this workflow?</AlertDialogTitle>
            <AlertDialogDescription>
              This workflow contains a send / notify action. Once active, it will send
              REAL messages to contacts on its next trigger. Activating is the only
              lock - are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingActivate) setActive(pendingActivate, true);
                setPendingActivate(null);
              }}
            >
              Yes, activate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
